import { Show, createEffect, onCleanup, onMount } from 'solid-js';
import { useTheme } from '../theme/ThemeProvider';

/**
 * Dot Matrix Background Component
 * Canvas-based animated halftone pattern
 * Creates wave motion with varying dot sizes
 */
export function DotMatrixBackground() {
  const theme = useTheme();
  let canvasRef: HTMLCanvasElement | undefined;
  let animationId: number | undefined;

  // Initialize and animate canvas
  const initCanvas = () => {
    if (!canvasRef) {
      return;
    }

    const ctx = canvasRef.getContext('2d');
    if (!ctx) {
      return;
    }

    // Set canvas size
    const resize = () => {
      if (canvasRef) {
        canvasRef.width = window.innerWidth;
        canvasRef.height = window.innerHeight;
      }
    };
    resize();
    window.addEventListener('resize', resize);

    // Animation loop
    const startTime = Date.now();
    const animate = () => {
      const currentTime = (Date.now() - startTime) / 1000;

      // Continue animation even if refs are temporarily unavailable
      if (!ctx || !canvasRef) {
        animationId = requestAnimationFrame(animate);
        return;
      }

      const width = canvasRef.width;
      const height = canvasRef.height;
      const isDark = theme.mode() === 'dark';

      // Clear canvas
      ctx.fillStyle = isDark ? '#000000' : '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Halftone parameters - larger dots for visibility
      const dotSpacing = 25;
      const waveFrequency = 0.008;
      const waveSpeed = 0.3;
      const maxDotSize = 10;

      // Draw halftone dots
      ctx.fillStyle = isDark ? '#ffffff' : '#000000';

      for (let x = 0; x < width + dotSpacing; x += dotSpacing) {
        for (let y = 0; y < height + dotSpacing; y += dotSpacing) {
          // Create wave pattern
          const waveX = x * waveFrequency;
          const waveY = y * waveFrequency;
          const wave =
            Math.sin(waveX + currentTime * waveSpeed) *
            Math.cos(waveY + currentTime * waveSpeed * 0.7);

          // Dot size based on wave (0 to maxDotSize)
          const dotSize = ((wave + 1) / 2) * maxDotSize;

          if (dotSize > 0.5) {
            ctx.beginPath();
            ctx.arc(x, y, dotSize / 2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    onCleanup(() => {
      window.removeEventListener('resize', resize);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    });
  };

  // Start animation on mount if dot-matrix is active
  onMount(() => {
    if (theme.preset() === 'dot-matrix') {
      // Delay to ensure canvas is rendered
      setTimeout(() => {
        initCanvas();
      }, 100);
    }
  });

  // Handle theme changes
  createEffect(() => {
    const isDotMatrix = theme.preset() === 'dot-matrix';

    if (isDotMatrix) {
      // If animation not running, start it
      if (!animationId) {
        setTimeout(() => {
          initCanvas();
        }, 50);
      }
    } else {
      // Stop animation when switching away
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = undefined;
      }
    }
  });

  return (
    <Show when={theme.preset() === 'dot-matrix'}>
      <canvas
        ref={canvasRef}
        class="dotmatrix-canvas"
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          'z-index': -2,
          'pointer-events': 'none',
        }}
      />
    </Show>
  );
}
